import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, Input, Select, Textarea } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { SubmitError } from '@/app/components/SubmitError';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { createCommunication, listCommunications } from '@/features/communications/api';
import { statusTone } from '@/features/communications/statusTone';
import {
  AUDIENCES,
  CATEGORIES,
  type Communication,
  type CommunicationAudience,
  type CommunicationCategory,
} from '@/features/communications/types';
import { useMe } from '@/features/me/MeContext';
import { Toggle } from '@/features/settings/Toggle';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/**
 * `/communications` — the Content pill's Communicatie composer + send log
 * (PLAN-FASE3.md WP-02). Same company-context pattern as `PilotsPage`: no
 * active company means nothing to compose or log, since a communication is
 * always scoped to one company. `companies.communications.*` is
 * company-scoped only (`permissions.ts`'s `canSeeCommunicationsSection`
 * doc, same shape as Challenges), so a coach alone gets the "not allowed"
 * state, same defense-in-depth as `ChallengesPage` for someone who
 * navigates here directly instead of through the (already-gated) nav. Live
 * phone preview and quickstart templates are explicitly "mag" for this
 * WP's first version — skipped.
 */
export function CommunicationsPage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const { permissions } = useMe();

  if (company === null) {
    return (
      <section className="page">
        <PageHeader title={t('communications.page.title')} />
        <p className="text-ink text-sm">{t('communications.page.noScope')}</p>
      </section>
    );
  }

  if (!permissions.canSeeCommunicationsSection) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('communications.page.title')} />
        <EmptyState text={t('communications.page.forbidden')} />
      </section>
    );
  }

  return <CompanyCommunications companyId={company.id} companyName={company.name} />;
}

function CompanyCommunications({
  companyId,
  companyName,
}: {
  companyId: string;
  companyName: string;
}) {
  const { t } = useTranslation();
  const communications = useAsync(() => listCommunications(companyId), [companyId]);
  const [composing, setComposing] = useState(false);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('communications.page.title')}
        lead={t('communications.page.lead', { company: companyName })}
        actions={
          <Button size="sm" onClick={() => setComposing((v) => !v)}>
            {composing ? t('communications.actions.cancel') : t('communications.actions.new')}
          </Button>
        }
      />

      {composing && (
        <Composer
          companyId={companyId}
          onDone={() => {
            setComposing(false);
            communications.reload();
          }}
        />
      )}

      <Card padding="none" aria-label={t('communications.log.title')}>
        <div className="p-5 pb-0">
          <CardHeader title={t('communications.log.title')} />
        </div>
        <AsyncSection state={communications} label={t('communications.log.title')} skeleton="table">
          {(rows) =>
            rows.length === 0 ? (
              <div className="p-5 pt-0">
                <EmptyState text={t('communications.log.empty')} />
              </div>
            ) : (
              <div className="p-5 pt-0">
                <SendLog rows={rows} />
              </div>
            )
          }
        </AsyncSection>
      </Card>
    </section>
  );
}

function SendLog({ rows }: { rows: Communication[] }) {
  const { t } = useTranslation();

  return (
    <DataTable
      rowKey={(row) => row.id ?? row.title ?? ''}
      columns={[
        {
          key: 'message',
          header: t('communications.log.columns.message'),
          render: (row) => <span className="text-ink text-[15px] font-medium">{row.title}</span>,
        },
        {
          key: 'category',
          header: t('communications.log.columns.category'),
          render: (row) => (row.category ? t(`communications.categories.${row.category}`) : '–'),
        },
        {
          key: 'reach',
          header: t('communications.log.columns.reach'),
          render: (row) => row.stats?.sent ?? 0,
        },
        {
          key: 'openRate',
          header: t('communications.log.columns.openRate'),
          render: (row) => `${row.stats?.open_rate ?? 0}%`,
        },
        {
          key: 'status',
          header: t('communications.log.columns.status'),
          align: 'end',
          render: (row) =>
            row.status ? (
              <Badge tone={statusTone(row.status)}>
                {t(`communications.status.${row.status}`)}
              </Badge>
            ) : null,
        },
      ]}
      rows={rows}
    />
  );
}

function Composer({ companyId, onDone }: { companyId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<CommunicationCategory>('algemeen');
  const [audience, setAudience] = useState<CommunicationAudience>('iedereen');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [callToAction, setCallToAction] = useState('');
  const [quietHours, setQuietHours] = useState(false);

  const scheduledAt = date === '' ? null : `${date} ${time === '' ? '00:00' : time}:00`;

  const form = useSubmit(async (status: 'concept' | 'ingepland') => {
    await createCommunication(companyId, {
      title,
      message,
      category,
      target_audience: audience,
      scheduled_at: scheduledAt,
      call_to_action: callToAction === '' ? null : callToAction,
      quiet_hours: quietHours,
      status,
    });
    onDone();
  });

  return (
    <Card aria-label={t('communications.composer.title')}>
      <CardHeader title={t('communications.composer.title')} />
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.submit(date === '' ? 'concept' : 'ingepland');
        }}
      >
        <Field label={t('communications.composer.titleField')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field label={t('communications.composer.message')}>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} required />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('communications.composer.category')}>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value as CommunicationCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`communications.categories.${c}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('communications.composer.audience')}>
            <Select
              value={audience}
              onChange={(e) => setAudience(e.target.value as CommunicationAudience)}
            >
              {AUDIENCES.map((a) => (
                <option key={a} value={a}>
                  {t(`communications.audiences.${a}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('communications.composer.date')}>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label={t('communications.composer.time')}>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </Field>
        </div>
        <Field label={t('communications.composer.callToAction')}>
          <Input value={callToAction} onChange={(e) => setCallToAction(e.target.value)} />
        </Field>
        <Toggle
          label={t('communications.composer.quietHours')}
          hint={t('communications.composer.quietHoursHint')}
          checked={quietHours}
          onChange={setQuietHours}
        />
        <div className="flex gap-2">
          <Button type="submit" disabled={form.pending}>
            {date === ''
              ? t('communications.composer.submit')
              : t('communications.composer.schedule')}
          </Button>
        </div>
        <SubmitError error={form.error} />
      </form>
    </Card>
  );
}
